import React__default, { forwardRef, useRef } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useRefObjectAsForwardedRef } from '../../hooks/useRefObjectAsForwardedRef.js';

function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
// --------------------------------------------------------
// Dropdown Root
/**
 *
 * Contains all the parts of a dropdown menu.
 */
const Root = ({
  children,
  ...props
}) => {
  return /*#__PURE__*/React__default.createElement(DropdownMenu.Root, props, children);
};
Root.displayName = "Root";
// --------------------------------------------------------
// Dropdown Trigger
/**
 *
 * The button that toggles the dropdown menu. By default, the DropdownMenu.Content will position itself against the trigger.
 *
 */
const Trigger = /*#__PURE__*/forwardRef(({
  children,
  ...props
}, forwardedRef) => {
  const innerRef = useRef(null);
  useRefObjectAsForwardedRef(forwardedRef, innerRef);
  return /*#__PURE__*/React__default.createElement(DropdownMenu.Trigger, _extends({
    ref: innerRef
  }, props), children);
});
// --------------------------------------------------------
// Dropdown Portal
/**
 *
 * When used, portals the content part into the body.
 *
 */
const Portal = ({
  children,
  container = document.body,
  ...props
}) => {
  return /*#__PURE__*/React__default.createElement(DropdownMenu.Portal, _extends({
    container: container
  }, props), children);
};
Portal.displayName = "Portal";
// --------------------------------------------------------
// Dropdown Content
/**
 *
 * The component that pops out when the dropdown menu is open.
 *
 */
const Content = ({
  children,
  ...props
}) => {
  const {
    asChild,
    loop,
    onCloseAutoFocus,
    onEscapeKeyDown,
    onPointerDownOutside,
    onFocusOutside,
    onInteractOutside,
    side = 'bottom',
    sideOffset = 0,
    align = 'center',
    alignOffset = 0,
    avoidCollisions = true,
    collisionBoundary,
    collisionPadding = 0,
    arrowPadding = 0,
    sticky = 'partial',
    hideWhenDetached = false,
    UNSAFE_className
  } = props;
  return /*#__PURE__*/React__default.createElement(DropdownMenu.Content, _extends({
    asChild: asChild,
    loop: loop,
    onCloseAutoFocus: onCloseAutoFocus,
    onEscapeKeyDown: onEscapeKeyDown,
    onPointerDownOutside: onPointerDownOutside,
    onFocusOutside: onFocusOutside,
    onInteractOutside: onInteractOutside,
    side: side,
    sideOffset: sideOffset,
    align: align,
    alignOffset: alignOffset,
    avoidCollisions: avoidCollisions,
    collisionBoundary: collisionBoundary,
    collisionPadding: collisionPadding,
    arrowPadding: arrowPadding,
    sticky: sticky,
    hideWhenDetached: true,
    className: UNSAFE_className
  }, props), children);
};
Content.displayName = "Content";
// --------------------------------------------------------
// Dropdown Arrow
/**
 *
 * An optional arrow element to render alongside the dropdown menu. This can be used to help visually link the trigger with the Dropdown.Content. Must be rendered inside Dropdown.Content.
 *
 */
const Arrow = ({
  children,
  asChild = false,
  UNSAFE_className,
  width = 10,
  height = 5,
  ...props
}) => {
  return /*#__PURE__*/React__default.createElement(DropdownMenu.Arrow, _extends({
    asChild: asChild,
    className: UNSAFE_className
  }, props), children);
};
Arrow.displayName = "Arrow";
// --------------------------------------------------------
// Dropdown Item
/**
 *
 * The component that contains the dropdown menu items.
 *
 */
const Item = ({
  children,
  ...props
}) => {
  const {
    asChild = false,
    disabled,
    onSelect,
    textValue,
    sx,
    UNSAFE_className
  } = props;
  return /*#__PURE__*/React__default.createElement(DropdownMenu.Item, _extends({
    asChild: asChild,
    disabled: disabled,
    onSelect: onSelect,
    textValue: textValue,
    sx: sx,
    UNSAFE_className: UNSAFE_className
  }, props), children);
};
Item.displayName = "Item";
// --------------------------------------------------------
// Dropdown Group
/**
 *
 * Used to group multiple DropdownMenu.Items
 *
 */
const Group = ({
  children,
  asChild = false,
  ...props
}) => {
  return /*#__PURE__*/React__default.createElement(DropdownMenu.Group, _extends({
    asChild: asChild
  }, props), children);
};
Group.displayName = "Group";
// --------------------------------------------------------
// Dropdown Label
/**
 *
 * Used to render a label. It won't be focusable using arrow keys.
 *
 */
const Label = ({
  children,
  asChild = false,
  UNSAFE_className,
  ...props
}) => {
  return /*#__PURE__*/React__default.createElement(DropdownMenu.Label, _extends({
    asChild: asChild,
    className: UNSAFE_className
  }, props), children);
};
Label.displayName = "Label";
// --------------------------------------------------------
// Dropdown CheckboxItem
/**
 *
 * An item that can be controlled and rendered like a checkbox.
 *
 */
const CheckboxItem = ({
  children,
  asChild = false,
  checked,
  onCheckedChange,
  disabled,
  onSelect,
  textValue,
  UNSAFE_className,
  ...props
}) => {
  return /*#__PURE__*/React__default.createElement(DropdownMenu.CheckboxItem, _extends({
    asChild: asChild,
    checked: checked,
    onCheckedChange: onCheckedChange,
    disabled: disabled,
    onSelect: onSelect,
    textValue: textValue,
    className: UNSAFE_className
  }, props), children);
};
CheckboxItem.displayName = "CheckboxItem";
// --------------------------------------------------------
// Dropdown RadioGroup
/**
 *
 * Used to group multiple DropdownMenu.RadioItems.
 *
 */
const RadioGroup = ({
  children,
  asChild = false,
  ...props
}) => {
  return /*#__PURE__*/React__default.createElement(DropdownMenu.RadioGroup, _extends({
    asChild: asChild
  }, props), children);
};
RadioGroup.displayName = "RadioGroup";
// --------------------------------------------------------
// Dropdown RadioItem
/**
 *
 * An item that can be controlled and rendered like a radio.
 *
 */
const RadioItem = ({
  children,
  value,
  onSelect,
  textValue,
  asChild = false,
  UNSAFE_className,
  ...props
}) => {
  return /*#__PURE__*/React__default.createElement(DropdownMenu.RadioItem, _extends({
    value: value,
    onSelect: onSelect,
    textValue: textValue,
    asChild: asChild,
    className: UNSAFE_className
  }, props), children);
};
RadioItem.displayName = "RadioItem";
// --------------------------------------------------------
// Dropdown Sub
/**
 *
 * Contains all the parts of a submenu.
 *
 */
const Sub = ({
  children,
  defaultOpen,
  open,
  onOpenChange,
  ...props
}) => {
  return /*#__PURE__*/React__default.createElement(DropdownMenu.Sub, _extends({
    defaultOpen: defaultOpen,
    open: open,
    onOpenChange: onOpenChange
  }, props), children);
};
Sub.displayName = "Sub";
// --------------------------------------------------------
// Dropdown SubTrigger
/**
 *
 * An item that opens a submenu. Must be rendered inside Dropdown.Sub
 *
 */
const SubTrigger = ({
  children,
  asChild,
  disabled,
  textValue,
  UNSAFE_className,
  ...props
}) => {
  return /*#__PURE__*/React__default.createElement(DropdownMenu.SubTrigger, _extends({
    asChild: asChild,
    disabled: disabled,
    textValue: textValue,
    className: UNSAFE_className
  }, props), children);
};
SubTrigger.displayName = "SubTrigger";
// --------------------------------------------------------
// Dropdown SubContent
/**
 *
 * The component that pops out when a submenu is open. Must be rendered inside Dropdown.Sub.
 *
 */
const SubContent = ({
  children,
  ...props
}) => {
  const {
    asChild = false,
    loop = false,
    onEscapeKeyDown,
    onPointerDownOutside,
    onFocusOutside,
    onInteractOutside,
    sideOffset = 0,
    alignOffset = 0,
    avoidCollisions = true,
    collisionBoundary,
    collisionPadding = 0,
    arrowPadding,
    sticky,
    hideWhenDetached,
    UNSAFE_className
  } = props;
  return /*#__PURE__*/React__default.createElement(DropdownMenu.SubContent, _extends({
    asChild: asChild,
    loop: loop,
    onEscapeKeyDown: onEscapeKeyDown,
    onPointerDownOutside: onPointerDownOutside,
    onFocusOutside: onFocusOutside,
    onInteractOutside: onInteractOutside,
    sideOffset: sideOffset,
    alignOffset: alignOffset,
    avoidCollisions: avoidCollisions,
    collisionBoundary: collisionBoundary,
    collisionPadding: collisionPadding,
    arrowPadding: arrowPadding,
    sticky: sticky,
    hideWhenDetached: hideWhenDetached,
    className: UNSAFE_className
  }, props), children);
};
SubContent.displayName = "SubContent";
// --------------------------------------------------------
// Dropdown Separator
/**
 *
 * Used to visually separate items in the dropdown menu.
 *
 */
const Separator = ({
  children,
  asChild,
  UNSAFE_className,
  ...props
}) => {
  return /*#__PURE__*/React__default.createElement(DropdownMenu.Separator, _extends({
    asChild: asChild,
    className: UNSAFE_className
  }, props), children);
};
Separator.displayName = "Separator";
// --------------------------------------------------------
// Dropdown ItemIndicator
/**
 *
 * Renders when the parent DropdownMenu.CheckboxItem or DropdownMenu.RadioItem is checked. You can style this element directly, or you can use it as a wrapper to put an icon into, or both.
 *
 */
const ItemIndicator = ({
  children,
  asChild,
  UNSAFE_className,
  ...props
}) => {
  return /*#__PURE__*/React__default.createElement(DropdownMenu.ItemIndicator, _extends({
    asChild: asChild,
    className: UNSAFE_className
  }, props), children);
};
ItemIndicator.displayName = "ItemIndicator";
// --------------------------------------------------------
// Exports
Root.displayName = 'Dropdown';
Trigger.displayName = 'Trigger';
Content.displayName = 'Content';
Item.displayName = 'Item';
Portal.displayName = 'Portal';
Content.displayName = 'Content';
Group.displayName = 'Group';
Sub.displayName = 'Sub';
SubTrigger.displayName = 'SubTrigger';
SubContent.displayName = 'SubContent';
Separator.displayName = 'Separator';
CheckboxItem.displayName = 'CheckboxItem';
ItemIndicator.displayName = 'ItemIndicator';
Label.displayName = 'Label';
RadioGroup.displayName = 'RadioGroup';
Arrow.displayName = 'Arrow';
RadioItem.displayName = 'RadioItem';
/**
 * Displays a menu to the user—such as a set of actions or functions—triggered by a button.
 */
var Dropdown = Object.assign(Root, {
  Trigger,
  Item,
  Content,
  Group,
  Sub,
  SubTrigger,
  Portal,
  SubContent,
  Separator,
  CheckboxItem,
  ItemIndicator,
  Label,
  RadioGroup,
  Arrow,
  RadioItem
});

export { Dropdown as default };
